<script setup lang="ts">
  import { ref, watchEffect } from "vue";
  import { useI18n } from "vue-i18n";
  import { IconArrowLeft, Text, Tag, Button, useNotification, IconLocked, IconUnlocked, IconLocator } from "@ui-kit";
  import { TopBar, Image, StatusBar } from "@src/components";
  import { type Product, useItemSetBlock, ResponseCode, useLazyProductsLoad, useLazyItemLoadByGuid } from "@src/api";
  import { useProfileOptions, useShopZoneHook, useOrderCreate } from "@src/hooks";
  import { useUserInfoStore, useWidgetsStore } from "@src/store";
  import { default as CardItemBlockDialog } from "./CardItemBlockDialog.vue";
  import { default as CardItemUnblockDialog } from "./CardItemUnblockDialog.vue";
  import { ItemZoneCarousel } from "../ItemZoneCarousel";
  import { SearchItemUHFDialog } from "../SearchItemUHFDialog";

  const { itemId = "" } = defineProps<{ itemId: string }>();
  const emit = defineEmits<{ (e: "close"): void; (e: "back"): void }>();

  const { t } = useI18n();
  const { hasPermission } = useProfileOptions();
  const userInfoStore = useUserInfoStore();
  const { mutateAsync: setBlock } = useItemSetBlock();
  const { execute: getProductsLoad } = useLazyProductsLoad();
  const { execute: getItemLoadByGuid } = useLazyItemLoadByGuid();
  const { shopZones } = useShopZoneHook();
  const { show } = useNotification();
  const order_create = useOrderCreate();
  const widgets_store = useWidgetsStore();

  const isContentHide = ref(false);
  const isBlockDialogOpen = ref(false);
  const isUnblockDialogOpen = ref(false);
  const visible_search_uhf_dialog = ref(false);
  const item = ref<(Product & { zones: { id: string; path: string; location: string }[] }) | null>(null);
  const itemSeria = ref<Product | null>(null);

  const handleCreateTaskClick = () => {
    emit("close");

    if (itemSeria.value) {
      order_create.add({ ...itemSeria.value });
      order_create.setStage("create");
      widgets_store.open("order_create");
    }
  };

  const handleSetBlock = async (blocked: boolean) => {
    const response = await setBlock({ item_id: itemId, blocked });

    if (response.code === ResponseCode.OK) {
      getItemAndZoneInfo();

      if (blocked) {
        isBlockDialogOpen.value = false;
      } else {
        isUnblockDialogOpen.value = false;
      }
    }
  };

  const getItemAndZoneInfo = async () => {
    try {
      const productResult = await getProductsLoad({ item_id: [itemId] });
      const _item = productResult.data.value.items.at(0);

      if (productResult.isError.value || !_item) {
        throw { title: t("Ошибка получения товара"), type: "error" };
      }

      const parentProductResult = await getItemLoadByGuid({
        guid: _item.seria_guid || `${_item.parent_guid}-${_item.article}`,
      });

      if (parentProductResult.isError.value) {
        throw { title: t("Ошибка получения Цветомодели"), type: "error" };
      }

      if (parentProductResult.data.value.item) {
        itemSeria.value = parentProductResult.data.value.item;
      }

      const item_zones: { id: string; path: string; location: string }[] = [];
      const zones = parentProductResult.data.value.zones.map((item) => shopZones.value.byId[item.zone_id]);

      zones.filter(Boolean).forEach((zone) => {
        const zone_ids = zone.hierarchy.split(".");
        let path = "";
        let location = "";

        if (zone_ids.length === 2) {
          path = shopZones.value.byId[zone_ids.at(0)].title;
          location = shopZones.value.byId[zone_ids.at(-1)].title;
        } else if (zone_ids.length > 2) {
          const path_origin = [
            shopZones.value.byId[zone_ids.at(0)].title.slice(0, 1),
            shopZones.value.byId[zone_ids.at(1)].title.slice(-2),
          ].join(" ");
          const path_other = zone_ids.slice(2, -1).map((id) => shopZones.value.byId[id].title);

          path = [path_origin, ...path_other].join(" • ");
          location = shopZones.value.byId[zone_ids.at(-1)].title;
        }

        item_zones.push({
          id: zone_ids.at(-1),
          path,
          location,
        });
      });

      item.value = { ..._item, zones: item_zones };
    } catch (error) {
      show({ title: error.title, type: error.type });
    }
  };

  const handleClickSize = (item: Product) => {
    visible_search_uhf_dialog.value = false;
    widgets_store.open("search_uhf", { item, index: 2 });
  };

  watchEffect(async () => {
    if (!itemId) {
      item.value = null;

      return;
    }

    await getItemAndZoneInfo();
  });
</script>

<template>
  <div class="card-item">
    <Image
      :url="item?.image_url"
      class="card-item__image"
      @click="isContentHide = !isContentHide"
      data-testid="card-image"
    />
    <div class="card-item__header">
      <StatusBar />
      <TopBar
        :title="item?.article || ''"
        :subtitle="`${item?.color}, ${item?.title}`"
        @click:left="emit('back')"
        @click:right="visible_search_uhf_dialog = true"
        data-testid="card-header"
      >
        <template #left>
          <IconArrowLeft />
        </template>
        <template #right>
          <IconLocator />
        </template>
      </TopBar>
    </div>
    <div class="card-item__content">
      <div class="card-item__details" :class="{ 'card-item__details--hide': isContentHide }">
        <div class="product-zone__info" data-testid="card-item-zone-info">
          <div class="product-zone__info-header">
            <Text font="body-medium-18" color="white" data-testid="card-item-article">{{ item?.article }}</Text>
            <Tag
              :text="item?.is_blocked ? $t('Заблокирован') : $t('Активен')"
              :variant="item?.is_blocked ? 'error' : 'success'"
              data-testid="card-item-blocked"
            />
          </div>
          <ItemZoneCarousel :zones="item?.zones" />
        </div>

        <div class="card-item__buttons">
          <template
            v-if="hasPermission('mobile.order_create') && userInfoStore.shop?.config.options.orders.includes('pickup')"
          >
            <Button @click="handleCreateTaskClick" data-testid="card-item-order-create-button">
              {{ $t("Вынести") }}
            </Button>
            <Button
              v-if="hasPermission('mobile.item_block') && item?.is_blocked"
              variant="secondary"
              @click="isUnblockDialogOpen = true"
              data-testid="card-item-unblock-btn"
              asSquare
            >
              <template #iconBefore>
                <IconUnlocked />
              </template>
            </Button>
            <Button
              v-else-if="hasPermission('mobile.item_block')"
              variant="secondary"
              @click="isBlockDialogOpen = true"
              data-testid="card-item-block-btn"
              asSquare
            >
              <template #iconBefore>
                <IconLocked />
              </template>
            </Button>
          </template>
          <template v-else>
            <Button
              v-if="hasPermission('mobile.item_block') && item?.is_blocked"
              class="card-item__unblock-btn"
              @click="isUnblockDialogOpen = true"
              data-testid="card-item-unblock-btn"
            >
              {{ $t("Разблокировать") }}
            </Button>
            <Button
              v-else-if="hasPermission('mobile.item_block')"
              class="card-item__block-btn"
              @click="isBlockDialogOpen = true"
              data-testid="card-item-block-btn"
            >
              {{ $t("Заблокировать") }}
            </Button>
          </template>
        </div>
      </div>
    </div>
  </div>
  <CardItemBlockDialog
    :isOpen="isBlockDialogOpen"
    @click:close="isBlockDialogOpen = false"
    @click:block="handleSetBlock(true)"
  />
  <CardItemUnblockDialog
    :itemId="itemId"
    :isOpen="isUnblockDialogOpen"
    @click:close="isUnblockDialogOpen = false"
    @click:unblock="handleSetBlock(false)"
  />
  <SearchItemUHFDialog
    :is_open="visible_search_uhf_dialog"
    :seria_guid="item?.is_virtual ? item?.external_id : item?.seria_guid"
    :img_url="item?.image_url"
    :article="item?.article"
    :name="item?.title"
    :color="item?.color"
    @click:close="visible_search_uhf_dialog = false"
    @click:size="handleClickSize"
  />
</template>

<style scoped>
  .card-item {
    display: flex;
    flex-direction: column;
    flex: 1;

    & .card-item__buttons {
      display: flex;
      gap: 8px;

      & :deep(.button):first-child {
        flex: 1;
      }
    }
  }

  .card-item__image {
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: initial;
    z-index: 1;
  }

  .card-item__image :deep(img:not(.image__default)) {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  .card-item__header {
    z-index: 1;
    background-color: var(--sf-base-background-card);
    backdrop-filter: blur(20px);
  }

  .card-item__content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex: 1;
  }

  .card-item__details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
    background: #14141499;
    border-radius: 16px;
    padding: 12px 16px;
    backdrop-filter: blur(20px);
    transition: bottom 0.3s ease-out;
    z-index: 2;
  }

  .card-item__details--hide {
    bottom: -132px;
  }

  .card-item__details :deep(.product-zone__info > .text) {
    color: #ffffff;
  }

  .card-item__zone-not-found {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .card-item__zone-not-found > :deep(.text) {
    color: #ffffff;
  }

  .zone-not-found__info {
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background-color: var(--sf-base-background-white);
  }

  .product-zone__info {
    display: flex;
    flex-direction: column;
    gap: 12px;

    & :deep(.item-zone-carousel__row) {
      padding: 17px 0px;
    }
  }

  .product-zone__info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    & :deep(.text) {
      color: #ffffff;
    }
  }

  .product-zone__carousel {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    gap: 8px;
  }

  .product-zone__carousel::-webkit-scrollbar {
    display: none;
  }

  .product-zone__zone {
    flex: 0 0 80%;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-secondary);
    border-radius: 12px;
    scroll-snap-align: center;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .product-zone__zone:only-child {
    flex: 1;
  }

  .product-zone__zone--active {
    transform: scale(1.02);
  }

  .product-zone__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
  }

  .product-zone__dots {
    display: flex;
    gap: 8px;
  }

  .product-zone__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    opacity: 0.4;
    background-color: var(--sf-base-border-dark);
  }

  .product-zone__dot--active {
    opacity: 1;
    background-color: var(--sf-base-border-dark);
  }

  .product-zone__row {
    display: flex;
  }

  .product-zone__row:not(:first-child) {
    border-top: 1px solid var(--sf-base-border-primary);
  }

  .product-zone__column {
    flex: 1;
    display: flex;
    justify-content: center;
    padding: 17px 2px;
  }

  .column--no-border {
    padding-top: 16px;
  }

  .product-zone__column:not(:first-child) {
    border-left: 1px solid var(--sf-base-border-primary);
  }
</style>
